import React, { useState, useEffect } from 'react';
import { getMonthlySalesReport } from '../../../actions/report';
import { getAllLocationPlain } from "../../../actions/warehouseAction";

import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  Statistic,
  Alert,
  Spin,
  Space,
  Typography,
  Tabs,
  Divider,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { Title } = Typography;

const SalesComparisonIndex = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  const [currentYearData, setCurrentYearData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const dispatch = (action) => action; 
      const locationsData = await getAllLocationPlain()(dispatch);
      setLocations(locationsData || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Fetch report 
  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch current year data
      const currentData = await getMonthlySalesReport(year, month, location);
      setCurrentYearData(currentData);

      // Fetch previous year data
      const previousData = await getMonthlySalesReport(year - 1, month, location);
      setPreviousYearData(previousData);
    } catch (err) {
      setError(err.message || 'Failed to load sales report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BD', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(2);
  };

  const yearOptions = [...Array(5)].map((_, i) => {
    const y = new Date().getFullYear() - i;
    return { value: y, label: y };
  });

  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ].map((m, i) => ({ value: i + 1, label: m }));

  const getLocationName = () => {
    if (!location) return null;
    const selectedLocation = locations.find(loc => loc.id === location);
    return selectedLocation ? selectedLocation.name || selectedLocation.title : null;
  };

  // Merge current and previous year data by day
  const getMergedData = () => {
    if (!currentYearData || !currentYearData.daily_sales || !previousYearData || !previousYearData.daily_sales) {
      return [];
    }

    const currentMap = new Map();
    currentYearData.daily_sales.forEach(item => {
      const day = new Date(item.date).getDate();
      currentMap.set(day, item);
    });

    const previousMap = new Map();
    console.log(previousMap)
    previousYearData.daily_sales.forEach(item => {
      const day = new Date(item.date).getDate();
      previousMap.set(day, item);
    });

    const allDays = new Set([...currentMap.keys(), ...previousMap.keys()]);
    
    return Array.from(allDays).sort((a, b) => a - b).map(day => {
      const current = currentMap.get(day) || {};
      const previous = previousMap.get(day) || {};
      
      return {
        day,
        current_date: current.date,
        previous_date: previous.date,
        current_invoices: current.total_invoices || 0,
        previous_invoices: previous.total_invoices || 0,
        current_bill: parseFloat(current.total_bill || 0),
        previous_bill: parseFloat(previous.total_bill || 0),
        current_payment: parseFloat(current.total_payment || 0),
        previous_payment: parseFloat(previous.total_payment || 0),
        current_due: parseFloat(current.total_due || 0),
        previous_due: parseFloat(previous.total_due || 0),
        current_quantity: current.total_quantity || 0,
        previous_quantity: previous.total_quantity || 0,
      };
    });
  };

  const comparisonColumns = [
    {
      title: 'Day',
      dataIndex: 'day',
      key: 'day',
      width: 60,
      fixed: 'left',
    },
    {
      title: `${year} Invoices`,
      dataIndex: 'current_invoices',
      key: 'current_invoices',
      align: 'left',
      width: 100,
    },
    {
      title: `${year} Sales`,
      dataIndex: 'current_bill',
      key: 'current_bill',
      align: 'left',
      width: 100,
      render: (amount) => formatCurrency(amount),
    },
    //     {
    //   title: `${year} Payments`,
    //   dataIndex: 'current_payment',
    //   key: 'current_payment',
    //   align: 'left',
    //   width: 100,
    //   render: (amount) => formatCurrency(amount),
    // },
    {
      title: `${year - 1} Invoices`,
      dataIndex: 'previous_invoices',
      key: 'previous_invoices',
      align: 'left',
      width: 100,
      render: (val) => <span style={{ color: '#999' }}>{val}</span>,
    },
    {
      title: `${year - 1} Sales`,
      dataIndex: 'previous_bill',
      key: 'previous_bill',
      align: 'left',
      width: 100,
      render: (amount) => <span style={{ color: '#999' }}>{formatCurrency(amount)}</span>,
    },
    {
      title: 'Change %',
      key: 'change_percent',
      align: 'center',
      width: 100,
      render: (_, record) => {
        const change = calculatePercentageChange(record.current_bill, record.previous_bill);
        const isPositive = change >= 0;
        return (
          <span style={{ color: isPositive ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
            {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(change)}%
          </span>
        );
      },
    },

    // {
    //   title: `${year - 1} Payments`,
    //   dataIndex: 'previous_payment',
    //   key: 'previous_payment',
    //   align: 'left',
    //   width: 100,
    //   render: (amount) => <span style={{ color: '#999' }}>{formatCurrency(amount)}</span>,
    // },
  ];

  const mergedData = getMergedData();

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Title level={2}>Year-over-Year Sales Comparison</Title>
      
      {/* Filters Section */}
      <Card style={{ marginBottom: 24 }}>
        <Space size="middle" wrap>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Year</div>
            <Select
              value={year}
              onChange={setYear}
              style={{ width: 120 }}
              options={yearOptions}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Month</div>
            <Select
              value={month}
              onChange={setMonth}
              style={{ width: 150 }}
              options={monthOptions}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Location</div>
            <Select
              value={location}
              onChange={setLocation}
              style={{ width: 250 }}
              placeholder="All Locations"
              allowClear
              loading={loadingLocations}
              showSearch
              filterOption={(input, option) =>
                (option && option.label ? option.label : '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {locations.map((loc) => (
                <Option key={loc.id} value={loc.id} label={loc.name || loc.title}>
                  <Space>
                    {loc.name || loc.title || `Location ${loc.id}`}
                  </Space>
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ paddingTop: 30 }}>
            <Button
              type="primary"
              onClick={fetchReport}
              loading={loading}
            >
              Generate Comparison
            </Button>
          </div>
        </Space>
      </Card>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" tip="Loading comparison report..." />
        </div>
      )}

      {/* Report Data */}
      {!loading && currentYearData && previousYearData && (
        <>
          {/* Summary Statistics Comparison */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={`Total Invoices (${year})`}
                  value={currentYearData.total_invoices}
                  // valueStyle={{ color: '#1890ff' }}
                />
                  <Divider/>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {year - 1}: {previousYearData.total_invoices} 
                  <span style={{ 
                    marginLeft: 8,
                    color: currentYearData.total_invoices >= previousYearData.total_invoices ? '#52c41a' : '#ff4d4f' 
                  }}>
                    ({calculatePercentageChange(currentYearData.total_invoices, previousYearData.total_invoices)}%)
                  </span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={`Total Sales (${year})`}
                  value={formatCurrency(currentYearData.total_sales)}
                  // valueStyle={{ color: '#52c41a' }}
                />
                  <Divider/>

                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {year - 1}: {formatCurrency(previousYearData.total_sales)}
                  <span style={{ 
                    marginLeft: 8,
                    color: currentYearData.total_sales >= previousYearData.total_sales ? '#52c41a' : '#ff4d4f' 
                  }}>
                    ({calculatePercentageChange(currentYearData.total_sales, previousYearData.total_sales)}%)
                  </span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={`Total Payments (${year})`}
                  value={formatCurrency(currentYearData.total_payments)}
                  // valueStyle={{ color: '#52c41a' }}
                />
                  <Divider/>

                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {year - 1}: {formatCurrency(previousYearData.total_payments)}
                  <span style={{ 
                    marginLeft: 8,
                    color: currentYearData.total_payments >= previousYearData.total_payments ? '#52c41a' : '#ff4d4f' 
                  }}>
                    ({calculatePercentageChange(currentYearData.total_payments, previousYearData.total_payments)}%)
                  </span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={`Total Due (${year})`}
                  value={formatCurrency(currentYearData.total_due)}
                  // valueStyle={{ color: '#ff4d4f' }}
                />
                  <Divider/>

                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {year - 1}: {formatCurrency(previousYearData.total_due)}
                  <span style={{ 
                    marginLeft: 8,
                    color: currentYearData.total_due <= previousYearData.total_due ? '#52c41a' : '#ff4d4f' 
                  }}>
                    ({calculatePercentageChange(currentYearData.total_due, previousYearData.total_due)}%)
                  </span>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Side-by-Side Comparison Table */}
          <Card
            title={
              <span>
                Yearly Comparison - {currentYearData.month_name} {year} vs {year - 1}
                {getLocationName() && ` - ${getLocationName()}`}
              </span>
            }
          >
            {mergedData.length > 0 ? (
              <Table
                columns={comparisonColumns}
                dataSource={mergedData}
                rowKey="day"
                pagination={{
                  pageSize: 31,
                  showTotal: (total) => `Total ${total} days`,
                  showSizeChanger: false,
                }}
                // scroll={{ x: 1200 }}
                summary={(pageData) => {
                  let currentInvoices = 0, previousInvoices = 0;
                  let currentBill = 0, previousBill = 0;
                  let currentPayment = 0, previousPayment = 0;

                  pageData.forEach((record) => {
                    currentInvoices += record.current_invoices;
                    previousInvoices += record.previous_invoices;
                    currentBill += record.current_bill;
                    previousBill += record.previous_bill;
                    currentPayment += record.current_payment;
                    previousPayment += record.previous_payment;
                  });

                  return (
                    <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                      <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="left">{currentInvoices}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="left">{formatCurrency(currentBill)}</Table.Summary.Cell>
                      {/* <Table.Summary.Cell index={6} align="left">{formatCurrency(currentPayment)}</Table.Summary.Cell> */}
                      <Table.Summary.Cell index={2} align="left">{previousInvoices}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="left">{formatCurrency(previousBill)}</Table.Summary.Cell>
                      {/* <Table.Summary.Cell index={7} align="left">{formatCurrency(previousPayment)}</Table.Summary.Cell> */}
                      {/* <Table.Summary.Cell index={5} align="center">
                        <span style={{ 
                          color: currentBill >= previousBill ? '#52c41a' : '#ff4d4f' 
                        }}>
                          {calculatePercentageChange(currentBill, previousBill)}%
                        </span>
                      </Table.Summary.Cell> */}
                    </Table.Summary.Row>
                  );
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                No sales data available for comparison
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default SalesComparisonIndex;